import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, BarChart3, Zap, HeadphonesIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Home = () => {
  const { t } = useLanguage();

  const services = [
  {
    icon: Users,
    title: t('services.crm.title'),
    desc: t('services.crm.desc'),
    color: '#007AFF'
  },
  {
    icon: BarChart3,
    title: t('services.marketing.title'),
    desc: t('services.marketing.desc'),
    color: '#34C759'
  },
  {
    icon: Zap,
    title: t('services.sales.title'),
    desc: t('services.sales.desc'),
    color: '#FF9500'
  },
  {
    icon: HeadphonesIcon,
    title: t('services.service.title'),
    desc: t('services.service.desc'),
    color: '#AF52DE'
  }];


  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F5F5F7] to-white" />
        
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl">

            <motion.p
              variants={fadeUp}
              className="text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-6">

              {t('hero.tagline')}
            </motion.p>
            
            <motion.h1
              variants={fadeUp}
              className="!font-['Plus_Jakarta_Sans'] !font-semibold !text-2xl !leading-[1.1] mb-6 text-[#1D1D1F]"
              data-testid="hero-title">

              {t('hero.title')}
            </motion.h1>
            
            <motion.p
              variants={fadeUp}
              className="text-lg md:text-xl text-[#86868B] leading-relaxed mb-10 max-w-2xl"
              data-testid="hero-subtitle">

              {t('hero.subtitle')}
            </motion.p>
            
            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  className="bg-[#1D1D1F] text-white hover:bg-black rounded-full px-8 py-3 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
                  data-testid="hero-cta-btn">

                  {t('hero.cta')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  variant="outline"
                  className="bg-white text-[#1D1D1F] border border-[#D2D2D7] hover:border-[#86868B] hover:bg-[#F5F5F7] rounded-full px-8 py-3 text-base font-medium transition-all"
                  data-testid="hero-secondary-btn">

                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute right-0 top-1/4 w-1/3 h-1/2 bg-gradient-to-l from-[#F5F5F7] to-transparent opacity-50" />
      </section>

      {/* Services Preview Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16">

            <h2 className="text-3xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
              {t('services.title')}
            </h2>
            <p className="text-lg text-[#86868B] max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) =>
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5, delay: index * 0.1 }}>

                <Link
                to="/services"
                className="group block h-full bg-[#F5F5F7] hover:bg-white p-8 md:p-10 rounded-3xl transition-all duration-300 border border-transparent hover:border-slate-200 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
                data-testid={`service-card-${service.title.toLowerCase()}`}>

                  <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${service.color}15` }}>

                    <service.icon className="w-6 h-6" style={{ color: service.color }} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1D1D1F] mb-3 font-['Plus_Jakarta_Sans']">
                    {service.title}
                  </h3>
                  <p className="text-[#86868B] leading-relaxed mb-4">
                    {service.desc}
                  </p>
                  <span className="inline-flex items-center text-[#007AFF] font-medium text-sm group-hover:gap-2 transition-all">
                    {t('services.learnMore')}
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* EU Focus Section */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5 }}>

              <h2 className="text-3xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-6 font-['Plus_Jakarta_Sans']">
                {t('about.euFocus.title')}
              </h2>
              <p className="text-lg text-[#86868B] leading-relaxed mb-8">
                {t('about.euFocus.text')}
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-white text-sm font-medium text-[#1D1D1F] border border-slate-200">
                  GDPR Compliant
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-white text-sm font-medium text-[#1D1D1F] border border-slate-200">
                  EU Data Centers
                </span>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-white text-sm font-medium text-[#1D1D1F] border border-slate-200">
                  Local Support
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative">

              <div className="aspect-[4/3] rounded-3xl overflow-hidden bg-white shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80"
                  alt="European business"
                  className="w-full h-full object-cover" />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto">

            <h2 className="text-3xl md:text-5xl font-semibold text-[#1D1D1F] tracking-tight mb-6 font-['Plus_Jakarta_Sans']">
              {t('hero.tagline')}
            </h2>
            <p className="text-lg text-[#86868B] mb-10">
              {t('hero.subtitle')}
            </p>
            <Link to="/contact">
              <Button
                className="bg-[#1D1D1F] text-white hover:bg-black rounded-full px-10 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
                data-testid="cta-section-btn">

                {t('hero.cta')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

};

export default Home;